import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ToppingValue } from "~/common/props/orchestrator";
import { ToppingValueDto } from "../../Product.type";
import { ToppingsType } from "./type";
import { TypeInventory } from "~/common/entities/general";

export const useLogicProductToppings = ({
  toppings,
  productVariations,
  handleToppings,
}: ToppingsType) => {
  const { t } = useTranslation();
  const [valueDataTopping, setValueDataTopping] = useState({});
  const [selectList, setSelectList] = useState<ToppingValueDto[]>([]);

  const isActive = useCallback(
    (itemToCheck: ToppingValue, parentIndex_: number) => {
      return selectList.find(
        (listItem) =>
          listItem.parentIndex === parentIndex_ &&
          listItem.value === itemToCheck.value,
      );
    },
    [selectList],
  );

  const addOrUpdate = useCallback(
    (topping: ToppingValueDto, parentIndex: number) => {
      const item = {
        ...topping,
        parentIndex,
      };
      let selectedToppings: ToppingValueDto[] = [];
      selectedToppings = selectList.filter((listItem: ToppingValueDto) => {
        return listItem.toppingId !== topping.toppingId;
      });
      selectedToppings.push(item);
      setValueDataTopping((i) => {
        return { ...i, [parentIndex]: topping.name };
      });
      selectedToppings = selectedToppings.sort(
        (a: ToppingValueDto, b: ToppingValueDto) => {
          return a?.value.localeCompare(b?.value);
        },
      );
      handleToppings(selectedToppings);
      setSelectList(selectedToppings);
    },
    [isActive, selectList],
  );

  useEffect(() => {
    const selectedTopping = toppings[0];
    if (toppings.length === 1 && !productVariations) {
      addOrUpdate(
        {
          ...selectedTopping.values[0],
          toppingId: selectedTopping.id,
          toppingName: selectedTopping.name,
        },
        0,
      );
    }
  }, [toppings]);

  useEffect(() => {
    if (productVariations && productVariations.length) {
      const variationsSort = [...productVariations].sort(
        (variationA, variationB) =>
          (variationA.priceWithDiscount || variationA.price) >
          (variationB.priceWithDiscount || variationB.price)
            ? 1
            : -1,
      );
      const items: ToppingValueDto[] = [];
      const currentVariation =
        variationsSort.find(
          (variation) => variation.inventory?.units || 0 > 0,
        ) ||
        variationsSort.find(
          (variation) =>
            variation.inventory &&
            variation.inventory?.type === TypeInventory.Always,
        ) ||
        variationsSort[0];
      currentVariation?.variations?.forEach((variation) => {
        const toppingIndex = toppings.findIndex(
          (_t) => _t.id === variation.toppingId,
        );
        const valueIndex = toppings[toppingIndex]?.values.findIndex(
          (_v) => _v.value === variation.value,
        );
        if (toppingIndex !== undefined && valueIndex !== undefined) {
          const topping = toppings[toppingIndex];
          const value = toppings[toppingIndex].values[valueIndex];
          if (value) {
            items.push({
              ...value,
              toppingName: topping.name,
              toppingId: topping.id,
              parentIndex: toppingIndex,
            });
          }
        }
      });

      const selectedToppings = items.sort(
        (a: ToppingValueDto, b: ToppingValueDto) => {
          return a?.value.localeCompare(b?.value);
        },
      );
      setSelectList(selectedToppings);
      setValueDataTopping((i) => {
        const valuesDataTopping = selectedToppings.reduce(
          (object, item) => ({
            ...object,
            [String(item.parentIndex)]: item.name,
          }),
          {},
        );
        return { ...i, ...valuesDataTopping };
      });
      handleToppings(selectedToppings);
    }
  }, [toppings]);

  return {
    t,
    valueDataTopping,
    isActive,
    addOrUpdate,
  };
};
